import React from "react";

const BLUE = "#4F7FFA";
const CYAN = "#00BCD4";

export const PRO_FEATURES = {
  scenarios: {
    icon: "⚖️",
    title: "What-If Scenarios",
    desc: "Compare up to 3 payoff strategies side by side",
  },
  variable_schedule: {
    icon: "📅",
    title: "Variable Payment Schedule",
    desc: "Plan different extra payments for upcoming months",
  },
  ellie: {
    icon: "💬",
    title: "Ellie AI Advisor",
    desc: "Get personalized AI-powered debt coaching",
  },
  milestones: {
    icon: "🏆",
    title: "Milestones",
    desc: "Track achievements and celebrate your progress",
  },
  unlimited_accounts: {
    icon: "➕",
    title: "Unlimited Accounts",
    desc: "Track more than 3 debt accounts",
  },
};

// Inline upgrade prompt shown inside a locked feature
export function ProGate({ feature, onUpgrade, compact = false }) {
  const f = PRO_FEATURES[feature] || {};
  if (compact) return (
    <div style={{
      background: "linear-gradient(135deg, #EEF2FF, #E0F2FE)",
      border: "1.5px solid #C7D2FE",
      borderRadius: 12, padding: "14px 16px",
      display: "flex", alignItems: "center", gap: 12,
    }}>
      <div style={{ fontSize: 24 }}>{f.icon || "🔒"}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1a2e" }}>{f.title} — Pro Feature</div>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{f.desc}</div>
      </div>
      <button onClick={onUpgrade} style={{
        background: BLUE, color: "#fff", border: "none", borderRadius: 8,
        padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
      }}>Upgrade</button>
    </div>
  );

  return (
    <div style={{
      textAlign: "center", padding: "40px 24px",
      background: "linear-gradient(135deg, #F8F9FF, #F0F9FF)",
      borderRadius: 16, border: "1.5px solid #C7D2FE",
    }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{f.icon || "🔒"}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: "#1a1a2e", marginBottom: 6 }}>{f.title}</div>
      <div style={{ fontSize: 14, color: "#64748b", marginBottom: 20, maxWidth: 280, margin: "0 auto 20px" }}>{f.desc}</div>
      <button onClick={onUpgrade} style={{
        background: `linear-gradient(135deg, ${BLUE}, #3B6FE8)`,
        color: "#fff", border: "none", borderRadius: 10,
        padding: "12px 28px", fontSize: 15, fontWeight: 700, cursor: "pointer",
        boxShadow: `0 4px 20px ${BLUE}55`,
      }}>Upgrade to Pro</button>
      <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 10 }}>$4.99/mo · Cancel anytime</div>
    </div>
  );
}

// Full upgrade modal
export function UpgradeModal({ onClose, onUpgrade, checkoutLoading = false }) {
  const [plan, setPlan] = React.useState("monthly");

  // On native (iOS/Android) the purchase goes through Apple/Google via RevenueCat,
  // NOT Stripe. Only the web flow redirects to Stripe. Showing "Redirecting to
  // Stripe..." on iOS is an App Store review risk (Guideline 3.1.1 mentions an
  // external payment processor), so keep that label web-only.
  const isNative = !!(window.Capacitor?.isNativePlatform?.());
  const loadingLabel = isNative ? "Processing..." : "Redirecting to Stripe...";

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 9999, padding: 24,
    }} onClick={onClose}>
      <div style={{
        background: "#fff", borderRadius: 20, maxWidth: 480, width: "100%",
        boxShadow: "0 24px 80px rgba(0,0,0,0.3)", overflow: "hidden",
        maxHeight: "90vh", overflowY: "auto",
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, #1a1a2e, #1e2a4a)`,
          padding: "16px 20px", position: "relative",
        }}>
          <button onClick={onClose} style={{
            position: "absolute", top: 12, right: 12,
            background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "50%",
            width: 28, height: 28, color: "#fff", fontSize: 14, cursor: "pointer",
          }}>✕</button>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <div style={{ border: `2px solid ${CYAN}`, borderRadius: 6, padding: "2px 8px" }}>
              <span style={{ fontWeight: 900, color: "#fff", fontSize: 14, letterSpacing: 2 }}>EL</span>
              <span style={{ fontWeight: 900, color: CYAN, fontSize: 14, letterSpacing: 2 }}>DE</span>
            </div>
            <div style={{
              background: `linear-gradient(135deg, ${BLUE}, ${CYAN})`,
              color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 8px",
              borderRadius: 20, letterSpacing: 1,
            }}>PRO</div>
          </div>
          <div style={{ color: "#fff", fontSize: 18, fontWeight: 800, marginBottom: 2 }}>
            Unlock your full potential
          </div>
          <div style={{ color: "#8899bb", fontSize: 12 }}>
            Everything you need to become debt-free faster
          </div>
        </div>

        {/* Features list */}
        <div style={{ padding: "8px 20px 0" }}>
          {Object.values(PRO_FEATURES).map(f => (
            <div key={f.title} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "5px 0", borderBottom: "1px solid #f1f5f9",
            }}>
              <div style={{ fontSize: 18, width: 26, textAlign: "center" }}>{f.icon}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e" }}>{f.title}</div>
                <div style={{ fontSize: 11, color: "#64748b" }}>{f.desc}</div>
              </div>
              <div style={{ marginLeft: "auto", color: "#10B981", fontSize: 14 }}>✓</div>
            </div>
          ))}
        </div>

        {/* Plan toggle */}
        <div style={{ padding: "8px 20px 0" }}>
          <div style={{
            display: "flex", background: "#F1F5F9", borderRadius: 10, padding: 3, gap: 3,
          }}>
            {[
              { key: "monthly", label: "Monthly", price: "$4.99/mo" },
              { key: "yearly", label: "Yearly", price: "$34.99/yr", badge: "Save 42%" },
            ].map(opt => (
              <button key={opt.key} onClick={() => setPlan(opt.key)} style={{
                flex: 1, padding: "8px 0", borderRadius: 8, border: "none",
                background: plan === opt.key ? "#fff" : "transparent",
                boxShadow: plan === opt.key ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                cursor: "pointer", transition: "all 0.2s",
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: plan === opt.key ? "#1a1a2e" : "#94a3b8" }}>
                  {opt.label}
                  {opt.badge && (
                    <span style={{
                      marginLeft: 6, fontSize: 10, fontWeight: 700,
                      background: "#10B981", color: "#fff",
                      padding: "2px 6px", borderRadius: 10,
                    }}>{opt.badge}</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: plan === opt.key ? BLUE : "#94a3b8", fontWeight: 600 }}>{opt.price}</div>
              </button>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div style={{ padding: "8px 20px 16px" }}>
          <button onClick={() => onUpgrade(plan)} disabled={checkoutLoading} style={{
            width: "100%", background: `linear-gradient(135deg, ${BLUE}, #3B6FE8)`,
            color: "#fff", border: "none", borderRadius: 12, padding: "12px 0",
            fontSize: 15, fontWeight: 700, cursor: checkoutLoading ? "not-allowed" : "pointer",
            boxShadow: `0 4px 20px ${BLUE}44`, opacity: checkoutLoading ? 0.7 : 1,
          }}>
            {checkoutLoading ? loadingLabel : plan === "yearly" ? "Upgrade to Pro — $34.99/yr" : "Upgrade to Pro — $4.99/mo"}
          </button>
          <div style={{ textAlign: "center", fontSize: 11, color: "#94a3b8", marginTop: 8 }}>
            Secure payment · Cancel anytime · No hidden fees
          </div>
        </div>
      </div>
    </div>
  );
}
